import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useForm } from "../../components/forms/useForm";
import { tenantAppTitle } from "../../shared/tenantBranding";
import { postForgotPassword } from "../../shared/passwordResetApi";

const initialValues = {
  email: "",
};

export default function ForgotPassword() {
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [globalError, setGlobalError] = useState("");
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [, setTick] = useState(0);

  const validate = (fieldValues = values) => {
    const temp = { ...errors };

    if ("email" in fieldValues) {
      if (!fieldValues.email || fieldValues.email.trim() === "") {
        temp.email = "Email is required.";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fieldValues.email.trim())) {
        temp.email = "Please enter a valid email address.";
      } else {
        temp.email = "";
      }
    }

    setErrors({ ...temp });

    if (fieldValues === values) {
      return Object.values(temp).every((x) => x === "");
    }
  };

  const { values, errors, setErrors, handleInputChange } = useForm(
    initialValues,
    true,
    validate
  );

  useEffect(() => {
    if (cooldownUntil <= Date.now()) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [cooldownUntil]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage("");
    setGlobalError("");

    if (!validate()) return;

    if (cooldownUntil > Date.now()) {
      return;
    }

    setLoading(true);
    const result = await postForgotPassword(values.email.trim());
    setLoading(false);

    if (result.ok && result.status === 200) {
      const msg =
        result.data?.message ||
        "If your email is registered, you will receive password reset instructions shortly.";
      setSuccessMessage(msg);
      return;
    }

    const status = result.status;
    const data = result.data || {};

    if (status === 422 && data.errors) {
      const first = Object.values(data.errors).flat()?.[0];
      setGlobalError(first || data.message || "Validation failed.");
    } else if (status === 429) {
      setGlobalError(
        data.message || "Please wait before requesting another password reset."
      );
      setCooldownUntil(Date.now() + 60 * 1000);
    } else if (status === 500) {
      setGlobalError(
        data.message || "Unable to send email. Please try again later."
      );
    } else {
      setGlobalError(data.message || "Something went wrong. Please try again.");
    }
  };

  const cooledDown = cooldownUntil > Date.now();
  const throttleSeconds = cooledDown
    ? Math.ceil((cooldownUntil - Date.now()) / 1000)
    : 0;

  return (
    <div className="h-full">
      <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <h2 className="mt-5 text-center text-3xl font-bold leading-9 tracking-tight text-gray-900">
            {tenantAppTitle(null)}
          </h2>
          <h2 className="mt-5 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
            Forgot password
          </h2>
          <p className="mt-3 text-center text-sm text-gray-600">
            Enter your email and we&apos;ll send you a link to reset your
            password.
          </p>
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
          {successMessage ? (
            <div className="rounded-md bg-green-50 p-4 text-sm text-green-800">
              {successMessage}
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Email address
                </label>
                <div className="mt-2">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    disabled={loading || cooledDown}
                    className="block w-full rounded-md border-0 py-1.5 pl-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 disabled:bg-gray-100"
                    onChange={handleInputChange}
                    value={values.email}
                  />
                  {errors.email ? (
                    <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                  ) : null}
                </div>
              </div>

              {globalError ? (
                <p className="text-sm text-red-600">{globalError}</p>
              ) : null}

              {cooledDown ? (
                <p className="text-sm text-amber-700">
                  You can submit again in {throttleSeconds} second
                  {throttleSeconds !== 1 ? "s" : ""}.
                </p>
              ) : null}

              <div>
                <button
                  type="submit"
                  disabled={loading || cooledDown}
                  className="flex w-full justify-center rounded-md bg-main-purple px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
                >
                  {loading ? "Sending…" : "Send reset link"}
                </button>
              </div>
            </form>
          )}

          <div className="mt-6 text-center text-sm">
            <Link
              to="/login"
              className="font-semibold text-indigo-600 hover:text-indigo-500"
            >
              Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
